import { Add, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Grid,
  Divider as MuiDivider,
  Paper,
  Typography,
  Link,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridRowEditStopReasons } from "@mui/x-data-grid";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Filters from "./Filters";
import { Data } from "./Data";
import DoughnutChart from "../rcAuto/DoughnutChart";
import BarChart from "../rcAuto/BarChart";
import { useGetRcTableColumns } from "../rcAuto/columns";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

function CVT() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [filtersActive, setFiltersActive] = useState(isDesktop);

  const {
    handlePageChange,
    handlePageSizeChange,
    handleApplyFilters,
    handleRemoveFilters,
    doughnutChartData,
    barChartData,
    rcData,
    totalRows,
    loading,
    filtersLoading,
    isApplyFiltersButtonDisabled,
    pageNumber,
    rowsPerPage,
    filterOptions,
    filterData,
    setFilterData,
    setFilterByDate,
    canClearFilters,
    isRemovingFilters,
    dateFilterNumber,
    setDateFilterNumber,
  } = Data();

  const navigate = useNavigate();
  const columns = useGetRcTableColumns();

  const switchFilterActive = () => {
    setFiltersActive((prev) => !prev);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    return updatedRow;
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            width: "calc(100vw - 40px)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Helmet title="CVT" />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h3" display="inline">
              CVT
            </Typography>
            <Box display="flex" justifyContent="end" gap={4}>
              <Button
                mr={2}
                variant="contained"
                color="primary"
                onClick={() => navigate("/pages/cvtDetails/")}
              >
                <Add />
              </Button>
            </Box>
          </Box>
          <Divider my={4} />
          <Grid container>
            {/* Charts */}
            <Grid
              container
              wrap="wrap"
              sx={{
                margin: isDesktop ? "0 -16px" : "0",
                padding: "0",
                width: isDesktop ? "calc(100% + 16px)" : "100%",
              }}
              spacing={isDesktop ? 4 : 0}
            >
              <Grid item lg={4} md={6} sm={12} xs={12} sx={{ flex: 1 }}>
                <DoughnutChart
                  doughnutChartData={doughnutChartData}
                  filterOptions={filterOptions}
                  setFilterByDate={setFilterByDate}
                  dateFilterNumber={dateFilterNumber}
                  setDateFilterNumber={setDateFilterNumber}
                />
              </Grid>
              <Grid item lg={8} md={6} sm={12} xs={12} sx={{ flex: 1 }}>
                <BarChart
                  barChartData={barChartData}
                  filterOptions={filterOptions}
                  setFilterByDate={setFilterByDate}
                  dateFilterNumber={dateFilterNumber}
                  setDateFilterNumber={setDateFilterNumber}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <>
                <Card sx={{ marginBottom: "1rem" }}>
                  <Filters
                    filtersActive={filtersActive}
                    switchFilterActive={switchFilterActive}
                    handleApplyFilters={handleApplyFilters}
                    handleRemoveFilters={handleRemoveFilters}
                    filtersLoading={filtersLoading}
                    isApplyFiltersButtonDisabled={isApplyFiltersButtonDisabled}
                    filterOptions={filterOptions}
                    filterData={filterData}
                    setFilterData={setFilterData}
                    canClearFilters={canClearFilters}
                    isRemovingFilters={isRemovingFilters}
                  />
                </Card>

                <Card mb={6}>
                  <Paper>
                    <Box sx={{ width: "100%" }}>
                      <DataGrid
                        autoHeight
                        pageSize={rowsPerPage}
                        onPageSizeChange={handlePageSizeChange}
                        rowsPerPageOptions={[5, 10, 20]}
                        pagination
                        paginationMode="server"
                        rowCount={totalRows}
                        page={pageNumber - 1}
                        onPageChange={(params) => handlePageChange(params)}
                        getRowId={(usersList) => usersList.idUID}
                        rows={rcData}
                        columns={columns}
                        editMode="row"
                        allowUpdating={true}
                        experimentalFeatures={{ newEditingApi: true }}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                        disableColumnMenu
                        disableSelectionOnClick
                        localeText={{ noRowsLabel: "Non ci sono risultati" }}
                        sx={{
                          "& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus":
                            {
                              outline: "none",
                            },
                          "& .MuiDataGrid-footerContainer": {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px",
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Card>
              </>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}

export default CVT;
